import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    createApi,
    fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

import { EnvConfig } from '@/configs/env.config';
import { SLICE_TAG } from '@/constants';
import { getRefreshTokenFromLocalStorage } from '@/utils';

import { clearAuthTokens, refreshSession, signOutSuccess } from './auth';

const baseQuery = fetchBaseQuery({
    baseUrl: EnvConfig.BASE_API_URL,
    prepareHeaders: (headers, { getState }: any) => {
        const token = getState().auth.session.accessToken;

        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result?.meta?.response?.status === 401) {
        console.warn('Access token expired.');

        const refreshToken = getRefreshTokenFromLocalStorage();

        if (!refreshToken) {
            console.error('No refresh token available.');
            clearAuthTokens();
            api.dispatch(signOutSuccess());
            return result;
        }

        const didRefresh = await refreshSession(
            refreshToken,
            api,
            extraOptions,
            refreshAccessToken,
        );

        if (didRefresh) {
            result = await baseQuery(args, api, extraOptions);
        } else {
            clearAuthTokens();
            api.dispatch(signOutSuccess());
        }
    }

    return result;
};

const refreshAccessToken = async (
    refreshToken: string,
    api: any,
    extraOptions: any,
) => {
    try {
        return await baseQuery(
            {
                url: '/auth/refresh-tokens',
                method: 'POST',
                body: { refresh_token: refreshToken },
                headers: { 'Content-Type': 'application/json' },
            },
            api,
            extraOptions,
        );
    } catch (error) {
        console.error('Error during token refresh:', error);
        throw error;
    }
};

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithReauth,
    tagTypes: Object.values(SLICE_TAG),
    endpoints: (_builder) => ({}),
});
